import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  IconButton,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  TextareaAutosize,
  Rating
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import HotelIcon from '@mui/icons-material/Hotel';
import BedIcon from '@mui/icons-material/Bed';
import PeopleIcon from '@mui/icons-material/People';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import StarIcon from '@mui/icons-material/Star';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LockIcon from '@mui/icons-material/Lock';
import HomeIcon from '@mui/icons-material/Home';
import ListIcon from '@mui/icons-material/List';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WifiIcon from '@mui/icons-material/Wifi';
import LocalParkingIcon from '@mui/icons-material/LocalParking';
import g2TravelApi from '../../services/g2TravelApi';
import { normalizeHotelRouteId, buildPaymentPath } from '../../lib/hotelRoutes';
import Header from '../../components/Header/Header';
import { useCurrency } from '../../context/CurrencyContext';
import './Booking.css';

const Booking = () => {
  const { formatFrom } = useCurrency();
  const { id: rawRouteId } = useParams();
  const id = useMemo(() => normalizeHotelRouteId(rawRouteId), [rawRouteId]);
  const location = useLocation();
  const navigate = useNavigate();

  const { hotel, searchParams, selectedOption, priceResults } = location.state || {};

  const [activeStep, setActiveStep] = useState(1);
  const [guestDetails, setGuestDetails] = useState({
      firstName: '',
      lastName: '',
    email: '',
    country: 'GB',
    nationality: 'GB',
    phone: '',
    phoneCode: '+44',
    isMainGuest: true,
    isBusinessTrip: false
  });
  const [specialRequests, setSpecialRequests] = useState('');
  const [arrivalTime, setArrivalTime] = useState('');
  const [addInsurance, setAddInsurance] = useState(false);
  const [addCarRental, setAddCarRental] = useState(false);
  const [addTaxi, setAddTaxi] = useState(false);
  const [booking, setBooking] = useState(false);
  const [error, setError] = useState(null);

  const steps = ['Your Selection', 'Your Details', 'Finish'];

  // Calculate check-out date
  const calculateCheckOut = (checkIn, nights) => {
    if (!checkIn) return '';
    const checkInDate = new Date(checkIn);
    checkInDate.setDate(checkInDate.getDate() + nights);
    return checkInDate.toISOString().split('T')[0];
  };

  const checkInDate = searchParams?.checkInDate || '';
  const nights = searchParams?.nights || 1;
  const checkOutDate = calculateCheckOut(checkInDate, nights);
  const adults = searchParams?.adults || 1;
  const children = searchParams?.children || 0;

  // Get star rating
  const getStarRating = (ratingString) => {
    if (!ratingString) return 0;
    const match = ratingString.match(/(\d+)/);
    return match ? parseInt(match[1]) : 0;
  };

  const starRating = getStarRating(hotel?.rating);

  // Calculate total price
  const totalPrice = selectedOption?.totalPrice || 0;
  const vatAmount = totalPrice * 0.20;
  const insurancePrice = addInsurance ? totalPrice * 0.055 : 0;
  const finalTotal = totalPrice + insurancePrice;

  // Handle form changes
  const handleInputChange = (field, value) => {
    setGuestDetails(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle booking submission
  const handleBooking = async () => {
    if (!guestDetails.firstName || !guestDetails.lastName || !guestDetails.email || !guestDetails.phone) {
      setError('Please fill in all required fields');
      return;
    }
    
      setBooking(true);
      setError(null);

    try {
      // Debug: Log available data
      console.log('🔍 Booking Debug - Available data:', {
        priceResults: priceResults,
        selectedOption: selectedOption,
        hotel: hotel,
        id: id,
        searchParams: searchParams
      });

      // Validate that we have the required data
      if (!priceResults) {
        console.error('❌ priceResults is missing');
        setError('Missing booking information. Please go back and try again.');
        setBooking(false);
        return;
      }

      if (!selectedOption) {
        console.error('❌ selectedOption is missing');
        setError('Missing booking information. Please go back and try again.');
        setBooking(false);
        return;
      }

      // Log full structure to understand the data (limit size to avoid console overflow)
      console.log('🔍 priceResults keys:', priceResults ? Object.keys(priceResults) : 'null');
      console.log('🔍 priceResults.propertyOption[0] keys:', priceResults?.propertyOption?.[0] ? Object.keys(priceResults.propertyOption[0]) : 'no propertyOption[0]');
      console.log('🔍 selectedOption keys:', selectedOption ? Object.keys(selectedOption) : 'null');
      
      // Check all possible locations for contextId
      console.log('🔍 Checking contextId locations:', {
        'priceResults.contextId': priceResults?.contextId,
        'priceResults.context?.contextId': priceResults?.context?.contextId,
        'priceResults.propertyOption[0]?.contextId': priceResults?.propertyOption?.[0]?.contextId,
        'priceResults.propertyOption[0]?.property?.contextId': priceResults?.propertyOption?.[0]?.property?.contextId,
        'selectedOption?.contextId': selectedOption?.contextId,
        'First option contextId': priceResults?.propertyOption?.[0]?.option?.[0]?.contextId
      });
      
      // Get required data from priceResults and selectedOption
      // contextId might be at different locations - try all possibilities
      const contextId = priceResults?.contextId ||
                        priceResults?.context?.contextId ||
                        priceResults?.propertyOption?.[0]?.contextId ||
                        priceResults?.propertyOption?.[0]?.property?.contextId ||
                        selectedOption?.contextId ||
                        priceResults?.propertyOption?.[0]?.option?.[0]?.contextId;
      
      // bookingKey should be at top level of priceResults
      // But sometimes it might be in the first propertyOption or selectedOption
      const bookingKey = priceResults?.bookingKey || 
                        priceResults?.propertyOption?.[0]?.bookingKey ||
                        selectedOption?.bookingKey;
      
      // propertyId can be in multiple places:
      // 1. In priceResults.propertyOption[0].propertyId (property level) - PRIMARY
      // 2. In selectedOption.propertyId (option level)
      // 3. In hotel.id or id (from URL params) - FALLBACK
      const propertyId = priceResults?.propertyOption?.[0]?.propertyId ||
                        selectedOption?.propertyId || 
                        hotel?.id || 
                        hotel?.propertyId ||
                        id;

      console.log('🔍 Booking Debug - Extracted values:', {
        contextId: contextId || 'MISSING',
        bookingKey: bookingKey ? bookingKey.substring(0, 20) + '...' : 'MISSING',
        propertyId: propertyId || 'MISSING',
        priceResultsHasContextId: !!priceResults?.contextId,
        priceResultsHasBookingKey: !!priceResults?.bookingKey,
        priceResultsPropertyOption: priceResults?.propertyOption?.[0] ? {
          hasPropertyId: !!priceResults?.propertyOption[0]?.propertyId,
          propertyId: priceResults?.propertyOption[0]?.propertyId
        } : 'NO PROPERTY OPTION',
        selectedOptionHasPropertyId: !!selectedOption?.propertyId,
        hotelId: hotel?.id,
        urlId: id
      });

      // Build detailed error message
      const missingFields = [];
      if (!contextId) missingFields.push('contextId');
      if (!bookingKey) missingFields.push('bookingKey');
      if (!propertyId) missingFields.push('propertyId');

      if (missingFields.length > 0) {
        console.error('❌ Missing required fields:', missingFields);
        console.error('❌ Full priceResults structure:', {
          hasContextId: !!priceResults?.contextId,
          contextId: priceResults?.contextId,
          hasBookingKey: !!priceResults?.bookingKey,
          bookingKey: priceResults?.bookingKey,
          hasPropertyOption: !!priceResults?.propertyOption,
          propertyOptionLength: priceResults?.propertyOption?.length,
          firstPropertyOption: priceResults?.propertyOption?.[0],
          allKeys: priceResults ? Object.keys(priceResults) : null
        });
        console.error('❌ selectedOption structure:', {
          hasPropertyId: !!selectedOption?.propertyId,
          propertyId: selectedOption?.propertyId,
          allKeys: selectedOption ? Object.keys(selectedOption) : null
        });
        setError(`Missing booking information: ${missingFields.join(', ')}. Please go back and try again.`);
        setBooking(false);
        return;
      }

      // Build passenger array - need one passenger per adult + child
      // CRITICAL: Each passenger MUST have an 'id' field (required by API)
      const passengers = [];
      const childAges = searchParams?.childAges || [];
      
      // Validate required guest details
      if (!guestDetails.firstName || !guestDetails.lastName) {
        setError('Please fill in all required fields (First name and Last name)');
        setBooking(false);
        return;
      }
      
      // Add main guest (first adult) - this is the lead passenger
      passengers.push({
        id: Number(1), // ✅ REQUIRED: Must be a number, starting from 1
        title: guestDetails.title || 'Mr', // Optional but recommended
        firstName: guestDetails.firstName, // Required
        lastName: guestDetails.lastName, // Required
        phone: guestDetails.phoneCode + guestDetails.phone, // Optional
        lead: true // ✅ REQUIRED: Must be boolean true for first passenger
      });
      
      // Add additional adults (if more than 1 adult)
      for (let i = 2; i <= adults; i++) {
        passengers.push({
          id: Number(i), // ✅ REQUIRED: Sequential ID (2, 3, 4...)
          title: 'Mr', // Default, can be made configurable if you collect this data
          firstName: guestDetails.firstName, // Using same name for now, can be made configurable
          lastName: guestDetails.lastName,
          phone: guestDetails.phoneCode + guestDetails.phone, // Optional
          lead: false // ✅ REQUIRED: Must be boolean false for additional passengers
        });
      }
      
      // Add children (if any)
      for (let i = 0; i < children; i++) {
        const childPassenger = {
          id: Number(adults + i + 1), // ✅ REQUIRED: Sequential ID after adults
          title: 'Mr', // Default for children
          firstName: guestDetails.firstName, // Using same name for now
          lastName: guestDetails.lastName,
          phone: guestDetails.phoneCode + guestDetails.phone, // Optional
          lead: false // ✅ REQUIRED: Must be boolean false for children
        };
        
        // Only add age if we have it and it's valid
        if (childAges[i] && childAges[i] > 0) {
          childPassenger.age = Number(childAges[i]);
        }
        
        passengers.push(childPassenger);
      }
      
      // Validate exactly one lead passenger
      const leadCount = passengers.filter(p => p.lead === true).length;
      if (leadCount !== 1) {
        console.error('❌ Invalid lead passenger count:', leadCount);
        setError('Booking validation error. Please try again.');
        setBooking(false);
        return;
      }

      // Extract room details from selectedOption
      const roomPrice = selectedOption?.roomPrice?.[0];
      const ratePlanCode = roomPrice?.ratePlan?.code || roomPrice?.ratePlanCode || '';
      const roomCode = roomPrice?.roomCode || '';
      
      // Validate required room fields
      if (!ratePlanCode || !roomCode) {
        console.error('❌ Missing room details:', {
          ratePlanCode,
          roomCode,
          roomPrice: roomPrice,
          selectedOption: selectedOption
        });
        setError('Missing room information. Please go back and try again.');
        setBooking(false);
        return;
      }

      // Structure roomPax correctly - passenger array should be inside roomPax
      const bookingParams = {
        contextId: contextId,
        bookingKey: bookingKey,
        propertyId: propertyId,
        distributionId: searchParams?.distributionId || "PL1662",
        nationality: searchParams?.nationality || guestDetails.nationality || guestDetails.country || 'GB',
        checkInDate: checkInDate,
        nights: nights,
        currency: 'GBP',
        roomPax: [{
          id: Number(1), // Required: room ID
          adults: Number(adults),
          children: Number(children),
          ratePlanCode: String(ratePlanCode), // Required: rate plan code
          roomCode: String(roomCode), // Required: room code
          ...(childAges.length > 0 && childAges.every(age => age > 0) ? { childAges: childAges.map(age => Number(age)) } : {}),
          passenger: passengers // Required: passenger array with id field in each passenger
        }],
        clientRef: `WEB-${guestDetails.firstName}${guestDetails.lastName}${Date.now()}`,
        timeoutMillis: 120000,
        includeRoomNames: true
      };

      // Navigate to Payment page with all booking data
      navigate(buildPaymentPath(id), {
        state: {
          hotel: hotel,
          searchParams: searchParams,
          selectedOption: selectedOption,
          priceResults: priceResults,
          guestDetails: guestDetails,
          bookingParams: bookingParams // Pass the prepared booking params
        }
      });
    } catch (err) {
      console.error('❌ Booking error:', err);
      console.error('❌ Error details:', {
        message: err.message,
        stack: err.stack
      });
      setError(err.message || 'Failed to create booking. Please try again.');
    } finally {
      setBooking(false);
    }
  };

  const handleGoHome = () => {
    navigate('/');
    window.scrollTo(0, 0);
  };

  const handleNavigate = (page) => {
    const routes = {
      'home': '/',
      'listing': '/hotels',
      'about': '/about',
      'contact': '/contact',
      'test-g2': '/test-g2'
    };
    navigate(routes[page] || '/');
    window.scrollTo(0, 0);
  };

  if (!hotel || !selectedOption) {
    return (
      <>
        <Header onLogoClick={handleGoHome} onNavigate={handleNavigate} />
        <Box sx={{ backgroundColor: '#f8f9fa', minHeight: '100vh', pb: 6 }}>
        <Container sx={{ mt: 4 }}>
          <Alert severity="error">Booking information missing</Alert>
          <Button onClick={() => navigate('/hotels')} sx={{ mt: 2 }}>
            Back to Hotels
          </Button>
        </Container>
        </Box>
      </>
    );
  }

    return (
      <>
        <Header onLogoClick={handleGoHome} onNavigate={handleNavigate} />
      <Box sx={{ backgroundColor: '#f5f5f5', minHeight: '100vh', pb: 6 }}>
        {/* Progress Stepper */}
        <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', py: 3 }}>
          <Container maxWidth="lg">
            <Stepper activeStep={activeStep} sx={{ pt: 2 }}>
              {steps.map((label, index) => (
                <Step key={label}>
                  <StepLabel
        sx={{ 
                      '& .MuiStepLabel-label': {
                        fontSize: '0.875rem',
                        fontWeight: index === activeStep ? 600 : 400,
                        color: index === activeStep ? '#0071C2' : '#666'
                      },
                      '& .MuiStepIcon-root': {
                        color: index < activeStep ? '#0071C2' : '#ccc',
                        '&.Mui-active': {
                          color: '#0071C2'
                  }
                }
              }}
            >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
        </Container>
      </Box>

        <Container maxWidth="lg" sx={{ 
          mt: 4,
          px: { xs: 0, sm: 3 },
          width: '100%',
          maxWidth: { xs: '100%', sm: 'lg' }
        }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' }, 
            gap: 4,
            px: { xs: 1, sm: 0 },
            width: '100%'
          }}>
            {/* LEFT COLUMN - Hotel Summary (35%) */}
            <Box sx={{ flex: { xs: '1 1 100%', md: '0 0 35%' }, maxWidth: { xs: '100%', md: '35%' } }}>
              {/* Hotel Summary Card */}
              <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: 'white', borderRadius: 2, border: '1px solid #e0e0e0', position: 'sticky', top: 20 }}>
                {/* Hotel Name & Rating */}
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#333', mb: 1 }}>
                  {hotel?.name}
            </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Rating value={starRating} readOnly size="small" sx={{ color: '#FDB022' }} />
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    {starRating}-Star Hotel
            </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: '#666', mb: 2, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationOnIcon sx={{ fontSize: 16 }} />
                  {hotel?.address1 || hotel?.address || 'Address not available'}
                </Typography>

                {/* Amenities */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                  <Chip icon={<WifiIcon />} label="WiFi" size="small" sx={{ backgroundColor: '#f5f5f5' }} />
                  <Chip icon={<LocalParkingIcon />} label="Parking" size="small" sx={{ backgroundColor: '#f5f5f5' }} />
            </Box>

                <Divider sx={{ my: 3 }} />

                {/* Booking Details */}
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', mb: 2 }}>
                  Your booking details
                </Typography>
                <Stack spacing={2} sx={{ mb: 3 }}>
                    <Box>
                    <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                      Check-in
                </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#333' }}>
                      {new Date(checkInDate).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                </Typography>
                    </Box>
                    <Box>
                    <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                      Check-out
                </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#333' }}>
                      {new Date(checkOutDate).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                </Typography>
                    </Box>
                  {nights === 1 && (
                    <Alert severity="warning" icon={<WarningIcon />} sx={{ py: 0.5, fontSize: '0.75rem' }}>
                      Just 1 day away!
                    </Alert>
                  )}
                    <Box>
                    <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                      Total
                </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#333' }}>
                      {nights} {nights === 1 ? 'night' : 'nights'}
                </Typography>
                    </Box>
                    <Box>
                    <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                  Guests
                </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#333' }}>
                      1 room for {adults} {adults === 1 ? 'adult' : 'adults'}
                </Typography>
                    </Box>
                </Stack>

              <Button
                  variant="text"
                  size="small"
                  sx={{ textTransform: 'none', color: '#0071C2', mb: 3 }}
                  onClick={() => navigate(-1)}
                >
                  Change your selection
              </Button>

                <Divider sx={{ my: 3 }} />

                {/* Price Summary */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', mb: 2 }}>
                    Your price summary
        </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#333' }}>
                      Total
        </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#333' }}>
                      {formatFrom(finalTotal, 'GBP')}
        </Typography>
      </Box>
                  <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                    Includes taxes
                  </Typography>
          <Button 
                    variant="text"
                    size="small"
                    sx={{ textTransform: 'none', color: '#0071C2', p: 0 }}
                    onClick={() => {}}
                  >
                    Price information
          </Button>
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      20% VAT: {formatFrom(vatAmount, 'GBP')}
              </Typography>
            </Box>
            </Box>
            
                <Divider sx={{ my: 3 }} />

                {/* Cancellation Policy */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', mb: 1 }}>
                    How much to cancel?
              </Typography>
                  <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                    If you cancel:
            </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#c62828' }}>
                    {formatFrom(finalTotal, 'GBP')}
                      </Typography>
                    </Box>

                {/* Urgency Message */}
                <Paper elevation={0} sx={{ p: 2, backgroundColor: '#fff3cd', borderRadius: 2, border: '1px solid #ffc107' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <AccessTimeIcon sx={{ color: '#f57c00', fontSize: 20 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#856404' }}>
                      Limited supply!
                      </Typography>
                    </Box>
                  <Typography variant="body2" sx={{ color: '#856404', fontSize: '0.75rem' }}>
                    556 homestays like this are already unavailable
                    </Typography>
                  </Paper>
                  </Paper>
                      </Box>

            {/* RIGHT COLUMN - Form Sections (65%) */}
            <Box sx={{ 
              flex: { xs: '1 1 100%', md: '0 0 65%' }, 
              maxWidth: { xs: '100%', md: '65%' },
              width: { xs: '100%', md: '65%' },
              px: { xs: 1, sm: 0 }
            }}>
              {/* Info Alert */}
                <Alert 
                  icon={<InfoIcon />}
                severity="info"
                sx={{ mb: 3, borderRadius: 2, backgroundColor: '#e3f2fd', color: '#1976d2' }}
              >
                Almost done! Fill in the <strong>* required</strong> information below to complete your booking.
                </Alert>

              {/* Guest Details Form */}
              <Paper elevation={0} sx={{ 
                p: { xs: 2, sm: 4 }, 
                mb: 3, 
                backgroundColor: 'white', 
                borderRadius: { xs: 0, sm: 2 },
                mx: { xs: -2, sm: 0 },
                width: { xs: 'calc(100% + 32px)', sm: 'auto' }
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', mb: 3 }}>
                  Enter your details
                        </Typography>

                <Grid container spacing={3} sx={{ width: '100%', margin: 0 }}>
                  <Grid item xs={12} sm={6} sx={{ width: '100%', padding: { xs: '8px !important', sm: '12px' } }}>
                            <TextField
                              fullWidth
                      label="First name"
                              required
                      value={guestDetails.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      sx={{ 
                        '& .MuiOutlinedInput-root': { borderRadius: 2 },
                        width: '100%',
                        maxWidth: '100%'
                      }}
                            />
                          </Grid>
                  <Grid item xs={12} sm={6} sx={{ width: '100%', padding: { xs: '8px !important', sm: '12px' } }}>
                            <TextField
                              fullWidth
                      label="Last name"
                              required
                      value={guestDetails.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      sx={{ 
                        '& .MuiOutlinedInput-root': { borderRadius: 2 },
                        width: '100%',
                        maxWidth: '100%'
                      }}
                            />
                          </Grid>

                  <Grid item xs={12} sx={{ width: '100%', padding: { xs: '8px !important', sm: '12px' } }}>
                    <TextField
                      fullWidth
                      label="Email address"
                      type="email"
                      required
                      value={guestDetails.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      helperText="Confirmation sent here"
                      sx={{ 
                        '& .MuiOutlinedInput-root': { borderRadius: 2 },
                        width: '100%',
                        maxWidth: '100%'
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sx={{ width: '100%', padding: { xs: '8px !important', sm: '12px' } }}>
                    <FormControl fullWidth sx={{ width: '100%', maxWidth: '100%' }}>
                      <InputLabel>Country/Region</InputLabel>
                      <Select
                        value={guestDetails.country}
                        onChange={(e) => handleInputChange('country', e.target.value)}
                        label="Country/Region"
                        sx={{ borderRadius: 2, width: '100%' }}
                      >
                        <MenuItem value="GB">United Kingdom</MenuItem>
                        <MenuItem value="US">United States</MenuItem>
                        <MenuItem value="CA">Canada</MenuItem>
                        <MenuItem value="AU">Australia</MenuItem>
                        <MenuItem value="DE">Germany</MenuItem>
                        <MenuItem value="FR">France</MenuItem>
                        <MenuItem value="IT">Italy</MenuItem>
                        <MenuItem value="ES">Spain</MenuItem>
                        <MenuItem value="NL">Netherlands</MenuItem>
                        <MenuItem value="BE">Belgium</MenuItem>
                        <MenuItem value="CH">Switzerland</MenuItem>
                        <MenuItem value="AT">Austria</MenuItem>
                        <MenuItem value="PT">Portugal</MenuItem>
                        <MenuItem value="IE">Ireland</MenuItem>
                        <MenuItem value="SE">Sweden</MenuItem>
                        <MenuItem value="NO">Norway</MenuItem>
                        <MenuItem value="DK">Denmark</MenuItem>
                        <MenuItem value="FI">Finland</MenuItem>
                        <MenuItem value="PL">Poland</MenuItem>
                        <MenuItem value="CZ">Czech Republic</MenuItem>
                        <MenuItem value="GR">Greece</MenuItem>
                        <MenuItem value="TR">Turkey</MenuItem>
                        <MenuItem value="RU">Russia</MenuItem>
                        <MenuItem value="JP">Japan</MenuItem>
                        <MenuItem value="CN">China</MenuItem>
                        <MenuItem value="IN">India</MenuItem>
                        <MenuItem value="KR">South Korea</MenuItem>
                        <MenuItem value="SG">Singapore</MenuItem>
                        <MenuItem value="MY">Malaysia</MenuItem>
                        <MenuItem value="TH">Thailand</MenuItem>
                        <MenuItem value="ID">Indonesia</MenuItem>
                        <MenuItem value="PH">Philippines</MenuItem>
                        <MenuItem value="VN">Vietnam</MenuItem>
                        <MenuItem value="NZ">New Zealand</MenuItem>
                        <MenuItem value="ZA">South Africa</MenuItem>
                        <MenuItem value="EG">Egypt</MenuItem>
                        <MenuItem value="AE">United Arab Emirates</MenuItem>
                        <MenuItem value="SA">Saudi Arabia</MenuItem>
                        <MenuItem value="IL">Israel</MenuItem>
                        <MenuItem value="BR">Brazil</MenuItem>
                        <MenuItem value="MX">Mexico</MenuItem>
                        <MenuItem value="AR">Argentina</MenuItem>
                        <MenuItem value="CL">Chile</MenuItem>
                        <MenuItem value="CO">Colombia</MenuItem>
                        <MenuItem value="PE">Peru</MenuItem>
                        <MenuItem value="IS">Iceland</MenuItem>
                        <MenuItem value="LU">Luxembourg</MenuItem>
                        <MenuItem value="MT">Malta</MenuItem>
                        <MenuItem value="CY">Cyprus</MenuItem>
                        <MenuItem value="HR">Croatia</MenuItem>
                        <MenuItem value="HU">Hungary</MenuItem>
                        <MenuItem value="RO">Romania</MenuItem>
                        <MenuItem value="BG">Bulgaria</MenuItem>
                        <MenuItem value="SK">Slovakia</MenuItem>
                        <MenuItem value="SI">Slovenia</MenuItem>
                        <MenuItem value="EE">Estonia</MenuItem>
                        <MenuItem value="LV">Latvia</MenuItem>
                        <MenuItem value="LT">Lithuania</MenuItem>
                        <MenuItem value="HK">Hong Kong</MenuItem>
                        <MenuItem value="TW">Taiwan</MenuItem>
                        <MenuItem value="MO">Macau</MenuItem>
                        <MenuItem value="KW">Kuwait</MenuItem>
                        <MenuItem value="QA">Qatar</MenuItem>
                        <MenuItem value="BH">Bahrain</MenuItem>
                        <MenuItem value="OM">Oman</MenuItem>
                        <MenuItem value="JO">Jordan</MenuItem>
                        <MenuItem value="LB">Lebanon</MenuItem>
                        <MenuItem value="PK">Pakistan</MenuItem>
                        <MenuItem value="BD">Bangladesh</MenuItem>
                        <MenuItem value="LK">Sri Lanka</MenuItem>
                        <MenuItem value="MM">Myanmar</MenuItem>
                        <MenuItem value="KH">Cambodia</MenuItem>
                        <MenuItem value="LA">Laos</MenuItem>
                        <MenuItem value="BN">Brunei</MenuItem>
                        <MenuItem value="FJ">Fiji</MenuItem>
                        <MenuItem value="PG">Papua New Guinea</MenuItem>
                        <MenuItem value="KE">Kenya</MenuItem>
                        <MenuItem value="NG">Nigeria</MenuItem>
                        <MenuItem value="GH">Ghana</MenuItem>
                        <MenuItem value="TZ">Tanzania</MenuItem>
                        <MenuItem value="UG">Uganda</MenuItem>
                        <MenuItem value="ET">Ethiopia</MenuItem>
                        <MenuItem value="MA">Morocco</MenuItem>
                        <MenuItem value="TN">Tunisia</MenuItem>
                        <MenuItem value="DZ">Algeria</MenuItem>
                        <MenuItem value="VE">Venezuela</MenuItem>
                        <MenuItem value="EC">Ecuador</MenuItem>
                        <MenuItem value="UY">Uruguay</MenuItem>
                        <MenuItem value="PY">Paraguay</MenuItem>
                        <MenuItem value="BO">Bolivia</MenuItem>
                        <MenuItem value="CR">Costa Rica</MenuItem>
                        <MenuItem value="PA">Panama</MenuItem>
                        <MenuItem value="GT">Guatemala</MenuItem>
                        <MenuItem value="DO">Dominican Republic</MenuItem>
                        <MenuItem value="JM">Jamaica</MenuItem>
                        <MenuItem value="TT">Trinidad and Tobago</MenuItem>
                        <MenuItem value="BB">Barbados</MenuItem>
                        <MenuItem value="BS">Bahamas</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sx={{ width: '100%', padding: { xs: '8px !important', sm: '12px' } }}>
                    <FormControl fullWidth sx={{ width: '100%', maxWidth: '100%' }}>
                      <InputLabel>Nationality</InputLabel>
                      <Select
                        value={guestDetails.nationality}
                        onChange={(e) => handleInputChange('nationality', e.target.value)}
                        label="Nationality"
                        sx={{ borderRadius: 2, width: '100%' }}
                      >
                        <MenuItem value="GB">United Kingdom</MenuItem>
                        <MenuItem value="US">United States</MenuItem>
                        <MenuItem value="CA">Canada</MenuItem>
                        <MenuItem value="AU">Australia</MenuItem>
                        <MenuItem value="DE">Germany</MenuItem>
                        <MenuItem value="FR">France</MenuItem>
                        <MenuItem value="IT">Italy</MenuItem>
                        <MenuItem value="ES">Spain</MenuItem>
                        <MenuItem value="NL">Netherlands</MenuItem>
                        <MenuItem value="BE">Belgium</MenuItem>
                        <MenuItem value="CH">Switzerland</MenuItem>
                        <MenuItem value="AT">Austria</MenuItem>
                        <MenuItem value="PT">Portugal</MenuItem>
                        <MenuItem value="IE">Ireland</MenuItem>
                        <MenuItem value="SE">Sweden</MenuItem>
                        <MenuItem value="NO">Norway</MenuItem>
                        <MenuItem value="DK">Denmark</MenuItem>
                        <MenuItem value="FI">Finland</MenuItem>
                        <MenuItem value="PL">Poland</MenuItem>
                        <MenuItem value="CZ">Czech Republic</MenuItem>
                        <MenuItem value="GR">Greece</MenuItem>
                        <MenuItem value="TR">Turkey</MenuItem>
                        <MenuItem value="RU">Russia</MenuItem>
                        <MenuItem value="JP">Japan</MenuItem>
                        <MenuItem value="CN">China</MenuItem>
                        <MenuItem value="IN">India</MenuItem>
                        <MenuItem value="KR">South Korea</MenuItem>
                        <MenuItem value="SG">Singapore</MenuItem>
                        <MenuItem value="MY">Malaysia</MenuItem>
                        <MenuItem value="TH">Thailand</MenuItem>
                        <MenuItem value="ID">Indonesia</MenuItem>
                        <MenuItem value="PH">Philippines</MenuItem>
                        <MenuItem value="VN">Vietnam</MenuItem>
                        <MenuItem value="NZ">New Zealand</MenuItem>
                        <MenuItem value="ZA">South Africa</MenuItem>
                        <MenuItem value="EG">Egypt</MenuItem>
                        <MenuItem value="AE">United Arab Emirates</MenuItem>
                        <MenuItem value="SA">Saudi Arabia</MenuItem>
                        <MenuItem value="IL">Israel</MenuItem>
                        <MenuItem value="BR">Brazil</MenuItem>
                        <MenuItem value="MX">Mexico</MenuItem>
                        <MenuItem value="AR">Argentina</MenuItem>
                        <MenuItem value="CL">Chile</MenuItem>
                        <MenuItem value="CO">Colombia</MenuItem>
                        <MenuItem value="PE">Peru</MenuItem>
                        <MenuItem value="IS">Iceland</MenuItem>
                        <MenuItem value="LU">Luxembourg</MenuItem>
                        <MenuItem value="MT">Malta</MenuItem>
                        <MenuItem value="CY">Cyprus</MenuItem>
                        <MenuItem value="HR">Croatia</MenuItem>
                        <MenuItem value="HU">Hungary</MenuItem>
                        <MenuItem value="RO">Romania</MenuItem>
                        <MenuItem value="BG">Bulgaria</MenuItem>
                        <MenuItem value="SK">Slovakia</MenuItem>
                        <MenuItem value="SI">Slovenia</MenuItem>
                        <MenuItem value="EE">Estonia</MenuItem>
                        <MenuItem value="LV">Latvia</MenuItem>
                        <MenuItem value="LT">Lithuania</MenuItem>
                        <MenuItem value="HK">Hong Kong</MenuItem>
                        <MenuItem value="TW">Taiwan</MenuItem>
                        <MenuItem value="MO">Macau</MenuItem>
                        <MenuItem value="KW">Kuwait</MenuItem>
                        <MenuItem value="QA">Qatar</MenuItem>
                        <MenuItem value="BH">Bahrain</MenuItem>
                        <MenuItem value="OM">Oman</MenuItem>
                        <MenuItem value="JO">Jordan</MenuItem>
                        <MenuItem value="LB">Lebanon</MenuItem>
                        <MenuItem value="PK">Pakistan</MenuItem>
                        <MenuItem value="BD">Bangladesh</MenuItem>
                        <MenuItem value="LK">Sri Lanka</MenuItem>
                        <MenuItem value="MM">Myanmar</MenuItem>
                        <MenuItem value="KH">Cambodia</MenuItem>
                        <MenuItem value="LA">Laos</MenuItem>
                        <MenuItem value="BN">Brunei</MenuItem>
                        <MenuItem value="FJ">Fiji</MenuItem>
                        <MenuItem value="PG">Papua New Guinea</MenuItem>
                        <MenuItem value="KE">Kenya</MenuItem>
                        <MenuItem value="NG">Nigeria</MenuItem>
                        <MenuItem value="GH">Ghana</MenuItem>
                        <MenuItem value="TZ">Tanzania</MenuItem>
                        <MenuItem value="UG">Uganda</MenuItem>
                        <MenuItem value="ET">Ethiopia</MenuItem>
                        <MenuItem value="MA">Morocco</MenuItem>
                        <MenuItem value="TN">Tunisia</MenuItem>
                        <MenuItem value="DZ">Algeria</MenuItem>
                        <MenuItem value="VE">Venezuela</MenuItem>
                        <MenuItem value="EC">Ecuador</MenuItem>
                        <MenuItem value="UY">Uruguay</MenuItem>
                        <MenuItem value="PY">Paraguay</MenuItem>
                        <MenuItem value="BO">Bolivia</MenuItem>
                        <MenuItem value="CR">Costa Rica</MenuItem>
                        <MenuItem value="PA">Panama</MenuItem>
                        <MenuItem value="GT">Guatemala</MenuItem>
                        <MenuItem value="DO">Dominican Republic</MenuItem>
                        <MenuItem value="JM">Jamaica</MenuItem>
                        <MenuItem value="TT">Trinidad and Tobago</MenuItem>
                        <MenuItem value="BB">Barbados</MenuItem>
                        <MenuItem value="BS">Bahamas</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={4} sx={{ width: '100%', padding: { xs: '8px !important', sm: '12px' } }}>
                    <FormControl fullWidth sx={{ width: '100%', maxWidth: '100%' }}>
                      <InputLabel>Phone code</InputLabel>
                      <Select
                        value={guestDetails.phoneCode}
                        onChange={(e) => handleInputChange('phoneCode', e.target.value)}
                        label="Phone code"
                        sx={{ borderRadius: 2, width: '100%' }}
                      >
                        <MenuItem value="+44">GB +44</MenuItem>
                        <MenuItem value="+1">US +1</MenuItem>
                        <MenuItem value="+61">AU +61</MenuItem>
                        <MenuItem value="+49">DE +49</MenuItem>
                        <MenuItem value="+33">FR +33</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={8} sx={{ width: '100%', padding: { xs: '8px !important', sm: '12px' } }}>
                    <TextField
                      fullWidth
                      label="Phone number"
                      required
                      value={guestDetails.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      sx={{ 
                        '& .MuiOutlinedInput-root': { borderRadius: 2 },
                        width: '100%',
                        maxWidth: '100%'
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600, color: '#333' }}>
                      Who are you booking for?
                    </FormLabel>
                <RadioGroup
                      value={guestDetails.isMainGuest ? 'main' : 'other'}
                      onChange={(e) => handleInputChange('isMainGuest', e.target.value === 'main')}
                    >
                      <FormControlLabel value="main" control={<Radio sx={{ color: '#0071C2' }} />} label="I'm the main guest" />
                      <FormControlLabel value="other" control={<Radio sx={{ color: '#0071C2' }} />} label="I'm booking for someone else" />
                </RadioGroup>
                  </Grid>

                  <Grid item xs={12}>
                    <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600, color: '#333' }}>
                      Are you traveling for work?
                    </FormLabel>
                <RadioGroup
                      value={guestDetails.isBusinessTrip ? 'yes' : 'no'}
                      onChange={(e) => handleInputChange('isBusinessTrip', e.target.value === 'yes')}
                    >
                      <FormControlLabel value="yes" control={<Radio sx={{ color: '#0071C2' }} />} label="Yes" />
                      <FormControlLabel value="no" control={<Radio sx={{ color: '#0071C2' }} />} label="No" />
                </RadioGroup>
                  </Grid>
                </Grid>
              </Paper>

              {/* Room Details Card */}
              <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: 'white', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', mb: 2 }}>
                  {selectedOption?.roomName || 'Room'}
                  </Typography>
                <Stack spacing={1} sx={{ mb: 2 }}>
                      <Chip 
                        label="Non-refundable" 
                        size="small" 
                    sx={{ backgroundColor: '#ffebee', color: '#c62828', fontWeight: 600 }}
                      />
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    👥 Guests: {adults} {adults === 1 ? 'adult' : 'adults'}{children > 0 ? `, ${children} ${children === 1 ? 'child' : 'children'}` : ''}
                    </Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    🚭 No smoking
                  </Typography>
                </Stack>
                <Button variant="text" size="small" sx={{ textTransform: 'none', color: '#0071C2' }}>
                  Add main guest details
                </Button>
                </Paper>

              {/* Insurance */}
              <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: 'white', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', mb: 1 }}>
                  Room Cancellation Insurance
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                  Protect {formatFrom(totalPrice, 'GBP')} for just {formatFrom(insurancePrice, 'GBP')}
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={addInsurance}
                      onChange={(e) => setAddInsurance(e.target.checked)}
                      sx={{ color: '#0071C2' }}
                    />
                  }
                  label="Add insurance"
                />
              </Paper>

              {/* Add to your stay */}
              <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: 'white', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', mb: 2 }}>
                  Add to your stay
                </Typography>
                <Stack spacing={1}>
                      <FormControlLabel
                        control={
                          <Checkbox 
                            checked={addCarRental}
                            onChange={(e) => setAddCarRental(e.target.checked)}
                            sx={{ color: '#0071C2' }}
                          />
                        }
                    label="Car rental with 5% off"
                  />
                      <FormControlLabel
                        control={
                          <Checkbox 
                            checked={addTaxi}
                            onChange={(e) => setAddTaxi(e.target.checked)}
                            sx={{ color: '#0071C2' }}
                          />
                        }
                    label="Taxi/shuttle in advance"
                  />
                </Stack>
              </Paper>

              {/* Special Requests */}
              <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: 'white', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', mb: 2 }}>
                  Special requests
                </Typography>
                <TextareaAutosize
                  minRows={3}
                  placeholder="Please let the property know what you need..."
                  value={specialRequests}
                  onChange={(e) => setSpecialRequests(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ccc',
                    borderRadius: '8px',
                    fontFamily: 'inherit',
                    fontSize: '0.875rem'
                  }}
                />
              </Paper>

              {/* Arrival Time */}
              <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: 'white', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', mb: 2 }}>
                  Your arrival time
                </Typography>
                <Typography variant="body2" sx={{ color: '#4caf50', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon sx={{ fontSize: 18 }} />
                  Check in: 5:00 PM - 8:00 PM
                  </Typography>
                <FormControl fullWidth>
                  <InputLabel>Please select</InputLabel>
                  <Select
                    value={arrivalTime}
                    onChange={(e) => setArrivalTime(e.target.value)}
                    label="Please select"
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="before-5pm">Before 5:00 PM</MenuItem>
                    <MenuItem value="5pm-8pm">5:00 PM - 8:00 PM</MenuItem>
                    <MenuItem value="after-8pm">After 8:00 PM</MenuItem>
                  </Select>
                </FormControl>
              </Paper>

              {/* Next Button */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 4 }}>
                <Button 
                  variant="contained" 
                  size="large"
                  onClick={handleBooking}
                  disabled={booking}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    backgroundColor: '#0071C2',
                    textTransform: 'none',
                        borderRadius: 2, 
                      px: 4,
                      py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                      '&:hover': {
                      backgroundColor: '#005a9e'
                    }
                  }}
                >
                  {booking ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Next: Final details'}
                  </Button>
                </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                  {error}
              </Alert>
              )}
                  </Box>
                  </Box>
        </Container>
      </Box>
    </>
  );
};

export default Booking;
